import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {IProjekt, isDraft, isDraftInProgressError, isExportable, Projekt} from '../../../shared/model/projekt.model';
import {listProjectFindings} from '../../../shared/model/projekt-validation';
import {ValidationFinding} from '../../../shared/model/validation-finding';
import {IFaehigkeit} from '../../../shared/model/faehigkeit.model';
import {DatePipe} from '@angular/common';
import {Berater} from '../../../shared/model/berater.model';
import {BeraterService, TaskMissingError} from '../../../services/berater.service';
import {Aufgabe} from '../../../shared/model/aufgabe.model';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MessageService} from '../../../shared/components/message.service';
import {MatDivider} from '@angular/material/divider';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ProjectComponent} from './project/project.component';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {ModalService} from '../../../shared/components/modal/modal.service';
import {DateService} from '../../../services/date.service';
import {MatCheckbox} from '@angular/material/checkbox';
import {ProjectsExportData} from '../../../shared/model/export-payload.model';
import {Badge} from '@spirit-testing/component-library';
import {MatTooltip} from '@angular/material/tooltip';
import {extractConstraintViolations, formatViolations} from '../../../shared/errors/constraint-violations';
import {ProfileSearchService} from '../profile-search/profile-search.service';
import {projectSearchTexts} from '../profile-search/project-search-texts';
import {VocabularyService} from '../../../services/vocabulary.service';

@Component({
  selector: 'app-projects',
  imports: [
    DatePipe,
    MatButton,
    MatIcon,
    MatDivider,
    TranslatePipe,
    ProjectComponent,
    ModalComponent,
    MatCheckbox,
    Badge,
    MatTooltip
  ],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectsComponent implements OnChanges, OnInit {
  @ViewChild('searchSlot') private searchSlot?: ElementRef<HTMLElement>;
  @ViewChild(ProjectComponent) private activeProjectComponent?: ProjectComponent;

  @Input() projects: IProjekt[];
  @Input() berater: Berater;
  @Input() exportMode: boolean;
  @Input() twoPagerMode: boolean = false;
  // Two-Pager: max. auswählbare Projekte, abhängig von der Seitenanzahl (1→0, 2→3, 3→6).
  @Input() maxProjects: number = 3;
  // Two-Pager: gemeinsame Skill-Auswahl (kontrolliert vom Parent, synchron mit der Fähigkeiten-Kachel).
  @Input() selectedSkillIds: string[] = [];
  @Input() skillBudgetReached: boolean = false;
  @Input() forcedProjectsData: ProjectsExportData | null = null;
  /** NP20-1057: nur der Profilbesitzer speichert Zwischenstände. */
  @Input() canSaveDraft = false;
  /** Aus dem Mail-Link der Entwurfs-Erinnerung oder dem Sprunglink „Zum Entwurf“. */
  @Input() openProjectUuid: string | null = null;
  private pendingOpenUuid: string | null = null;

  @Output() projectSaved = new EventEmitter<IProjekt[]>();
  @Output() faehigkeitenReloaded = new EventEmitter<IFaehigkeit[]>();
  @Output() exportSelectionChange = new EventEmitter<string[]>();
  @Output() methodSelectionChange = new EventEmitter<string[]>();
  @Output() skillSelectionChange = new EventEmitter<string[]>();
  @Output() projectsExportDataChange = new EventEmitter<ProjectsExportData>();

  selectedExportIds: string[] = [];
  selectedTaskExportIds: string[] = [];
  methodSelections: Record<string, string[]> = {};
  taskSelections: Record<string, string[]> = {};

  // Methodik-Chips sind pro Projekt begrenzt (nicht mehr global).
  readonly MAX_METHODS_PER_PROJECT = 3;

  getRemainingMethodSlots(_projectUuid: string): number {
    return this.MAX_METHODS_PER_PROJECT;
  }
  activeProject: IProjekt;
  newProject: IProjekt;
  projectCopy: IProjekt[];
  isNewProject: boolean = false;
  showModal = false;
  isSaving = false;
  failedSave: IProjekt | null = null;
  resumeEditUuid: string | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly profileSearch = inject(ProfileSearchService, {optional: true});
  // Die Suche fragt die Texte bei jeder Eingabe und jeder DOM-Änderung ab.
  private readonly searchTextCache = new Map<IProjekt, string[]>();

  constructor(
    private beraterService: BeraterService,
    private messageService: MessageService,
    private modalService: ModalService,
    private dateService: DateService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private vocabulary: VocabularyService,
  ) {
  }

  ngOnInit() {
    this.projectCopy = Projekt.cloneArray(this.projects ?? []);
    this.rebuildProjectFindings();
    this.registerSearchSource();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.searchTextCache.clear();
        this.rebuildProjectFindings();
        this.cdr.markForCheck();
      });
    this.vocabulary.changes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.searchTextCache.clear());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projects'] && changes['projects'].currentValue) {
      this.projectCopy = Projekt.cloneArray(changes['projects'].currentValue);
      this.searchTextCache.clear();
      if (this.activeProject?.uuid) {
        this.activeProject = this.projects?.find(p => p.uuid === this.activeProject.uuid) ?? null;
      }
      this.rebuildProjectFindings();
    }

    // Der Link greift genau einmal; jedes Speichern liefert neue `projects` und sprang sonst zurück auf ihn.
    if (changes['openProjectUuid']) {
      this.pendingOpenUuid = this.openProjectUuid;
    }
    if (this.pendingOpenUuid && !this.isEditingActiveProject()) {
      const linked = this.projects?.find(p => p.uuid === this.pendingOpenUuid);
      if (linked) {
        this.pendingOpenUuid = null;
        if (linked !== this.activeProject) this.selectProject(linked);
      }
    }

    const exportOrModeChanged = !!(changes['exportMode'] || changes['twoPagerMode']);
    const projectsDataChanged = !!(changes['projects'] && changes['projects'].currentValue);

    if (exportOrModeChanged || projectsDataChanged) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAll();
      } else if (!this.exportMode) {
        this.selectedExportIds = [];
        this.selectedTaskExportIds = [];
        this.methodSelections = {};
        this.taskSelections = {};
      } else if (this.twoPagerMode && exportOrModeChanged) {
        this.selectedExportIds = [];
        this.selectedTaskExportIds = [];
        this.taskSelections = {};
        this.methodSelections = {};
        this.exportSelectionChange.emit([]);
        this.emitProjectsExportData();
      }
    }

    if (changes['forcedProjectsData'] && this.forcedProjectsData !== null) {
      this.selectedExportIds = [...(this.forcedProjectsData.selectedProjectUuids ?? [])];
      this.taskSelections = {...this.forcedProjectsData.taskSelections};
      this.methodSelections = {...this.forcedProjectsData.methodSelections};
      this.exportSelectionChange.emit([...this.selectedExportIds]);
      this.emitProjectsExportData();
    }

    if (changes['maxProjects'] && !changes['maxProjects'].firstChange && this.twoPagerMode) {
      this.enforceMaxProjects();
    }
  }

  // Beim Reduzieren der Seitenanzahl überzählige Projekte (und deren Task-/Methodik-Auswahl) verwerfen.
  private enforceMaxProjects(): void {
    if (this.selectedExportIds.length <= this.maxProjects) return;
    this.selectedExportIds = this.selectedExportIds.slice(0, this.maxProjects);
    const kept = new Set(this.selectedExportIds);
    for (const uuid of Object.keys(this.taskSelections)) {
      if (!kept.has(uuid)) delete this.taskSelections[uuid];
    }
    for (const uuid of Object.keys(this.methodSelections)) {
      if (!kept.has(uuid)) delete this.methodSelections[uuid];
    }
    this.exportSelectionChange.emit([...this.selectedExportIds, ...this.selectedTaskExportIds]);
    this.emitProjectsExportData();
  }

  private autoSelectAll(): void {
    const exportable = (this.projects ?? []).filter(isExportable);
    this.selectedExportIds = exportable.map(p => p.uuid).filter(Boolean);
    this.taskSelections = {};
    for (const project of exportable) {
      if (project.uuid) {
        this.taskSelections[project.uuid] = (project.tasks ?? []).map(t => t.uuid).filter((uuid): uuid is string => !!uuid);
      }
    }
    this.methodSelections = {};
    this.exportSelectionChange.emit([...this.selectedExportIds]);
    this.emitProjectsExportData();
  }

  toggleExportSelection(id: string, checked: boolean): void {
    if (checked) {
      if (!isExportable(this.projects?.find(p => p.uuid === id))) return;
      if (this.twoPagerMode && this.selectedExportIds.length >= this.maxProjects) return;
      if (!this.selectedExportIds.includes(id)) {
        this.selectedExportIds.push(id);
      }
      // NP20-853: Beim Anwählen eines Projekts dessen Aufgaben automatisch mitauswählen.
      // Manuelles Abwählen einzelner Aufgaben bleibt möglich (child emitiert dann back
      // via onTaskExportSelectionChange und überschreibt diese Liste).
      const project = (this.projects ?? []).find(p => p.uuid === id);
      this.taskSelections[id] = (project?.tasks ?? [])
        .map(t => t.uuid)
        .filter((uuid): uuid is string => !!uuid);
    } else {
      this.selectedExportIds = this.selectedExportIds.filter(i => i !== id);
      // Beim Abwählen des Projekts auch dessen Task-Auswahl räumen.
      delete this.taskSelections[id];
    }
    this.selectedTaskExportIds = Object.values(this.taskSelections).flat();
    this.exportSelectionChange.emit([...this.selectedExportIds, ...this.selectedTaskExportIds]);
    this.emitProjectsExportData();
  }

  // Methoden statt `readonly isDraft = isDraft`: Im gemeinsamen Vitest-Lauf (`ng test`) waren importierte
  // Werte in Feld-Initializern schon zweimal undefined (NP20-861 INPUT_LIMITS, NP20-1167 RequestType), einzeln
  // grün. Die Ursache ist nicht geklärt; ein Import-Zyklus ließ sich nicht nachweisen.
  isDraftProject(project: IProjekt): boolean {
    return isDraft(project);
  }

  isExportableProject(project: IProjekt): boolean {
    return isExportable(project);
  }

  canSelectProject(): boolean {
    return !this.twoPagerMode || this.selectedExportIds.length < this.maxProjects;
  }

  onTaskExportSelectionChange(ids: string[], projectUuid: string): void {
    if (!isExportable(this.projects?.find(p => p.uuid === projectUuid))) ids = [];
    if (projectUuid) this.taskSelections[projectUuid] = ids;
    this.selectedTaskExportIds = ids;
    this.exportSelectionChange.emit([...this.selectedExportIds, ...this.selectedTaskExportIds]);
    this.emitProjectsExportData();
  }

  onProjectMethodSelectionChange(ids: string[], projectUuid: string): void {
    if (projectUuid) this.methodSelections[projectUuid] = ids;
    this.methodSelectionChange.emit(Object.values(this.methodSelections).flat());
    this.emitProjectsExportData();
  }

  private emitProjectsExportData(): void {
    this.projectsExportDataChange.emit({
      selectedProjectUuids: [...this.selectedExportIds],
      taskSelections: {...this.taskSelections},
      methodSelections: {...this.methodSelections}
    });
  }

  selectProject(projekt: IProjekt) {
    this.failedSave = null;
    this.resumeEditUuid = null;
    this.isNewProject = false;
    this.activeProject = this.activeProject === projekt ? null : projekt;
    if (this.activeProject?.uuid) {
      this.taskSelections[this.activeProject.uuid] ??= [];
      this.methodSelections[this.activeProject.uuid] ??= [];
    }
  }

  /**
   * NP20-824: Nur das aktive Projekt ist gerendert. Die übrigen liefern ihre Suchtexte
   * aus dem Modell und werden beim Sprung aktiviert — nicht, solange das aktive Projekt
   * bearbeitet wird, sonst gingen die ungespeicherten Änderungen verloren.
   */
  private registerSearchSource(): void {
    if (!this.profileSearch) return;
    const unregister = this.profileSearch.registerSource({
      section: 'projects',
      slot: () => this.searchSlot?.nativeElement ?? null,
      activeKey: () => this.activeProject?.uuid ?? null,
      entries: () => {
        const editing = this.isEditingActiveProject();
        return (this.projects ?? [])
          .filter(project => !!project.uuid)
          .map(project => ({
            key: project.uuid as string,
            texts: editing || project === this.activeProject ? [] : this.searchTextsOf(project),
          }));
      },
      activate: key => {
        if (this.isEditingActiveProject()) return;
        const project = key ? this.projects?.find(p => p.uuid === key) : null;
        if (key && !project) return;
        if ((this.activeProject?.uuid ?? null) === (project?.uuid ?? null)) return;
        if (project) {
          this.selectProject(project);
        } else {
          this.activeProject = null;
        }
        this.cdr.markForCheck();
      },
    });
    this.destroyRef.onDestroy(unregister);
  }

  private isEditingActiveProject(): boolean {
    return !!this.activeProjectComponent?.editMode;
  }

  private searchTextsOf(project: IProjekt): string[] {
    let texts = this.searchTextCache.get(project);
    if (!texts) {
      texts = projectSearchTexts(project, this.translate, this.vocabulary);
      this.searchTextCache.set(project, texts);
    }
    return texts;
  }

  /** `draft`: Zwischenstand ohne Prüfung und ohne Dublettenabgleich (NP20-1057). */
  changeProjectData(event: IProjekt, draft = false) {
    const idx = this.projects.findIndex((p) => p === this.activeProject);
    const mapped: IProjekt = {
      ...event,
      beginn: this.dateService.toLocalDateString(event.beginn),
      ende: this.dateService.toLocalDateString(event.ende),
    };

    if (!event.uuid) {
      const isDuplicate = this.projects.some(p =>
        p.projektname === event.projektname &&
        p.kunde === event.kunde &&
        this.dateService.toLocalDateString(p.beginn) === mapped.beginn &&
        this.dateService.toLocalDateString(p.ende) === mapped.ende
      );

      if (isDuplicate && !draft) {
        this.failedSave = mapped;
        this.messageService.error('projects.duplicateError');
        return;
      }

      this.isSaving = true;
      const create$ = draft
        ? this.beraterService.createProjectDraft(mapped, this.berater.id)
        : this.beraterService.createProject(mapped, this.berater.id);
      create$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({project, faehigkeiten}) => {
            // NP20-781: der Comparator bestimmt die Position, sonst stimmt sie erst nach einem Reload.
            this.projects.push(project);
            this.projects.sort(Projekt.compareByRecency);
            const savedProjects = Projekt.cloneArray(this.projects);
            this.projectCopy = savedProjects;
            this.isNewProject = false;
            this.failedSave = null;
            this.resumeEditUuid = draft ? project.uuid ?? null : null;
            this.activeProject = project;
            this.isSaving = false;
            this.projectSaved.emit(savedProjects);
            this.faehigkeitenReloaded.emit(faehigkeiten);
            this.messageService.success(draft ? 'project.draft.savedMessage' : 'projects.successUpdateMessage');
            this.cdr.markForCheck();
          },
          error: (err: unknown) => {
            this.isSaving = false;
            this.failedSave = mapped;
            this.reportSaveError(err);
            this.cdr.markForCheck();
          },
        });
    } else {
      // Rollback-Snapshot vor der optimistischen Mutation ziehen, da projectCopy durch den Emit überschrieben wird.
      const rollbackState = Projekt.cloneArray(this.projects);
      if (idx !== -1) {
        this.projects[idx] = mapped;
      }
      this.activeProject = mapped;
      this.isSaving = true;
      this.projectSaved.emit(Projekt.cloneArray(this.projects));

      const update$ = draft
        ? this.beraterService.saveProjectDraft(mapped, this.berater.id, event.uuid)
        : this.beraterService.updateProject(mapped, this.berater.id, event.uuid);
      update$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({project, faehigkeiten}) => {
            if (idx !== -1) {
              this.projects[idx] = project;
            }
            // NP20-781: erst hier sortieren, nach dem letzten idx-Zugriff, damit der Index gültig bleibt.
            this.projects.sort(Projekt.compareByRecency);
            const savedProjects = Projekt.cloneArray(this.projects);
            this.projectCopy = savedProjects;
            this.isNewProject = false;
            this.failedSave = null;
            if (!draft) this.resumeEditUuid = null;
            this.activeProject = project;
            this.isSaving = false;
            this.projectSaved.emit(savedProjects);
            this.faehigkeitenReloaded.emit(faehigkeiten);
            this.messageService.success(draft ? 'project.draft.savedMessage' : 'projects.successUpdateMessage');
            this.cdr.markForCheck();
          },
          error: (err: unknown) => {
            this.projects = rollbackState;
            this.projectCopy = Projekt.cloneArray(rollbackState);
            this.activeProject = this.projects.find(p => p.uuid === event.uuid) ?? null;
            this.isNewProject = false;
            this.isSaving = false;
            this.projectSaved.emit(Projekt.cloneArray(this.projects));
            this.failedSave = mapped;
            this.reportSaveError(err);
            this.cdr.markForCheck();
          }
        });
    }
  }

  saveTask(task: Aufgabe) {
    const projectId = this.activeProject?.uuid;
    if (!projectId || this.isSaving) return;
    this.isSaving = true;
    this.beraterService.saveProjectTask(task, this.berater.id, projectId, this.canSaveDraft)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({project, faehigkeiten}) => {
          const idx = this.projects.findIndex(p => p.uuid === projectId);
          if (idx !== -1) this.projects[idx] = project;
          this.projects.sort(Projekt.compareByRecency);
          const savedProjects = Projekt.cloneArray(this.projects);
          this.projectCopy = savedProjects;
          this.activeProject = project;
          this.isSaving = false;
          this.projectSaved.emit(savedProjects);
          this.faehigkeitenReloaded.emit(faehigkeiten);
          this.messageService.success('project.taskSave.savedMessage');
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isSaving = false;
          if (err instanceof TaskMissingError) {
            this.messageService.error('project.taskSave.missing');
          } else {
            this.reportUpdateError(err);
          }
          this.cdr.markForCheck();
        },
      });
  }

  createProject() {
    this.failedSave = null;
    this.resumeEditUuid = null;
    this.newProject = new Projekt();
    this.activeProject = this.newProject;
    this.isNewProject = true;
  }

  deleteProject() {
    const projectId = this.activeProject?.uuid;
    if (!projectId) {
      return;
    }

    const idx = this.projects.findIndex((p) => p === this.activeProject);

    this.beraterService.deleteProjectById(projectId, this.berater.id).subscribe({
      next: faehigkeiten => {
        if (idx !== -1) {
          this.projects.splice(idx, 1);
        }
        this.activeProject = null;
        const remainingProjects = Projekt.cloneArray(this.projects);
        this.projectCopy = remainingProjects;
        this.projectSaved.emit(remainingProjects);
        // NP20-760: Faehigkeiten des geloeschten Projekts sonst bis zum Neuladen in der Liste.
        this.faehigkeitenReloaded.emit(faehigkeiten);
        this.messageService.success('projects.successDeleteMessage');
        this.cdr.markForCheck();
      },
      error: (err: unknown) => {
        this.messageService.error(isDraftInProgressError(err) ? 'project.draft.lockedHint' : 'projects.errorDeleteMessage');
      },
    });
  }

  onCancelProject(): void {
    this.isNewProject = false;
    this.resumeEditUuid = null;
    this.failedSave = null;
  }

  openModal() {
    this.showModal = true;
    this.modalService.open('projectModal');
  }

  showDiscardModal = false;

  /** Der Papierkorb löscht bei einem Entwurf über einer Freigabe auch den freigegebenen Stand. */
  get deleteHitsReleasedVersion(): boolean {
    return isDraft(this.activeProject) && isExportable(this.activeProject);
  }

  openDiscardModal() {
    this.showDiscardModal = true;
    this.modalService.open('discardDraftModal');
  }

  discardActiveDraft() {
    const projectId = this.activeProject?.uuid;
    if (!projectId) return;
    this.isSaving = true;
    this.beraterService.discardProjectDraft(this.berater.id, projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({project, faehigkeiten}) => {
          const idx = this.projects.findIndex(p => p.uuid === projectId);
          if (idx !== -1) this.projects[idx] = project;
          this.projects.sort(Projekt.compareByRecency);
          const savedProjects = Projekt.cloneArray(this.projects);
          this.projectCopy = savedProjects;
          this.activeProject = project;
          this.isSaving = false;
          this.projectSaved.emit(savedProjects);
          this.faehigkeitenReloaded.emit(faehigkeiten);
          this.messageService.success('project.draft.discardedMessage');
          this.cdr.markForCheck();
        },
        error: () => {
          this.isSaving = false;
          this.messageService.error('project.draft.discardError');
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * NP20-828: Cache fuer die pro-Projekt-Fehlerzahl, damit das Template nicht in
   * jedem Change-Detection-Zyklus (3x pro Karte: @if, aria-label, {{}}) den vollen
   * Aufgabe.validate()-Loop laeuft. Wird bei jedem Wechsel von `projects` in
   * ngOnChanges neu befuellt.
   */
  private projectErrorCounts = new Map<string, number>();
  private projectErrorHovers = new Map<string, string>();

  private static readonly MAX_HOVER_FINDINGS = 5;
  private static readonly MAX_TASK_TITLE_LENGTH = 60;

  getProjectErrorCount(project: IProjekt): number {
    return this.projectErrorCounts.get(project?.uuid ?? '') ?? 0;
  }

  getProjectErrorHover(project: IProjekt): string {
    return this.projectErrorHovers.get(project?.uuid ?? '') ?? '';
  }

  private rebuildProjectFindings(): void {
    const counts = new Map<string, number>();
    const hovers = new Map<string, string>();
    for (const project of (this.projects ?? [])) {
      if (!project?.uuid) continue;
      const findings = listProjectFindings(project);
      counts.set(project.uuid, findings.length);
      if (findings.length === 0) continue;
      const entries = findings.map(finding => this.describeFinding(finding));
      hovers.set(project.uuid, this.buildErrorHover(entries));
    }
    this.projectErrorCounts = counts;
    this.projectErrorHovers = hovers;
  }

  private buildErrorHover(entries: string[]): string {
    const shown = entries.slice(0, ProjectsComponent.MAX_HOVER_FINDINGS);
    const lines = shown.map(entry => `\u2022 ${entry}`);
    const hidden = entries.length - shown.length;
    if (hidden > 0) {
      lines.push(this.translate.instant('projects.validationHoverMore', {count: hidden}));
    }
    return [this.headline(entries.length), ...lines].join('\n');
  }

  private headline(count: number): string {
    return count === 1
      ? this.translate.instant('projects.validationHoverHeadlineSingular')
      : this.translate.instant('projects.validationHoverHeadlinePlural', {count});
  }

  private describeFinding(finding: ValidationFinding): string {
    const field = this.translate.instant(finding.labelKey);
    if (!finding.task) return field;
    const number = finding.task.index + 1;
    const title = this.shortenTaskTitle(finding.task.caption);
    return title
      ? this.translate.instant('projects.validationHoverTaskWithTitle', {number, title, field})
      : this.translate.instant('projects.validationHoverTask', {number, field});
  }

  private shortenTaskTitle(caption: string | undefined): string {
    const trimmed = caption?.trim() ?? '';
    if (trimmed.length <= ProjectsComponent.MAX_TASK_TITLE_LENGTH) return trimmed;
    return `${trimmed.slice(0, ProjectsComponent.MAX_TASK_TITLE_LENGTH).trimEnd()}\u2026`;
  }

  private reportSaveError(err: unknown): void {
    if (isDraftInProgressError(err)) {
      this.messageService.error('project.draft.lockedHint');
      return;
    }
    this.reportUpdateError(err);
  }

  private reportUpdateError(err: unknown): void {
    const violations = extractConstraintViolations(err);
    if (violations) {
      this.messageService.errorWithDetail('projects.errorUpdateMessage', formatViolations(violations));
      return;
    }
    this.messageService.error('projects.errorUpdateMessage');
  }
}
